import { CONTACT_SUPPORT_URL, DEPLOYMENT_DOCS_URL } from '@common/constants';
import { Private_Routes } from '@common/url';
import { DeploymentAnalytics } from '@common/user-analytics/deployment';
import { ArrowNarrowUpRightIcon, ArrowRightIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Card, Text } from '@prophecy/ui-v3';
import { Stack } from '@prophecy/ui-v3/Layout';
import styled from 'styled-components';

import { useMixPanel } from '../../context/mixpanel/context';

const InfoCard = styled(Card)`
  min-width: 252px;
`;

const GetStartedContainer = styled(Stack)`
  height: 100%;
`;

const InfoLinkContainer = styled.div`
  margin-top: ${theme.spaces.x16};
`;

const Container = styled(Stack)`
  padding-bottom: 30px;
  max-height: 619px;
  height: 100%;
`;

export default function Deployment() {
  const { track } = useMixPanel();

  return (
    <Container direction='horizontal' gap={theme.spaces.x32}>
      <Card>
        <GetStartedContainer align='space-between'>
          <Stack gap={theme.spaces.x12}>
            <Text elementType='h1' weight={theme.fontWeight.medium} level='xl'>
              Prophecy Private Deployment
            </Text>
            <Text elementType='p' tone={theme.colors.gray600} level='sm'>
              The Prophecy Private Deployment empowers users to install Prophecy within their Virtual Private Cloud
              (VPC). Ensuring that users possess the necessary permissions for a successful deployment is of utmost
              importance. To assist users throughout the deployment process, comprehensive information is available in
              the Prophecy documentation. <br /> <br />
              This deployment tool equips administrators with a meticulously crafted, step-by-step guide, facilitating
              the setup of suitable infrastructures and the deployment of Prophecy using Helm Charts. Additionally, the
              tool provides scripts that generate the essential templates and scripts required for a seamless deployment
              experience.
            </Text>
          </Stack>
          <Stack alignY='end'>
            <Button
              onClick={() => {
                track(
                  DeploymentAnalytics.deployment_settings.events['deployment.settings.getStarted'],
                  {},
                  DeploymentAnalytics.deployment_settings.event_category
                );
              }}
              elementType='link'
              to={Private_Routes.Deployment.getUrl()}
              minWidth={240}
              icon={<ArrowRightIcon type='default' />}
              data-test-id='deployment-get-started-button'>
              Get Started
            </Button>
          </Stack>
        </GetStartedContainer>
      </Card>
      <Stack gap={theme.spaces.x16}>
        <InfoCard>
          <Stack gap={theme.spaces.x8}>
            <Text weight='bold' elementType='h1' level='sm'>
              System Requirements &amp; Documentation
            </Text>
            <Text elementType='p' tone={theme.colors.gray600} level='sm'>
              Explore our comprehensive documentation to ensure your infrastructure meets the minimum system
              requirements and find everything you need to get started.
            </Text>
          </Stack>
          <InfoLinkContainer>
            <Button
              elementType='anchor'
              target='_blank'
              href={DEPLOYMENT_DOCS_URL}
              icon={<ArrowNarrowUpRightIcon type='default' />}
              onClick={() => {
                track(
                  DeploymentAnalytics.deployment_settings.events['deployment.settings.documentation'],
                  {},
                  DeploymentAnalytics.deployment_settings.event_category
                );
              }}
              variant='secondaryGray'
              data-test-id='deployment-documentation-button'>
              Documentation
            </Button>
          </InfoLinkContainer>
        </InfoCard>
        <InfoCard>
          <Stack gap={theme.spaces.x8}>
            <Text weight='bold' elementType='h1' level='sm'>
              Need more help?
            </Text>
            <Text elementType='p' tone={theme.colors.gray600} level='sm'>
              Connect with a Prophecy Specialist for assistance with your self-hosted installation.
            </Text>
          </Stack>
          <InfoLinkContainer>
            <Button
              elementType='anchor'
              target='_blank'
              href={CONTACT_SUPPORT_URL}
              variant='secondaryGray'
              onClick={() => {
                track(
                  DeploymentAnalytics.deployment_settings.events['deployment.settings.contactSupport'],
                  {},
                  DeploymentAnalytics.deployment_settings.event_category
                );
              }}
              data-test-id='deployment-contact-support-button'>
              Contact Support
            </Button>
          </InfoLinkContainer>
        </InfoCard>
      </Stack>
    </Container>
  );
}
